import { useEffect, useRef, useState } from 'react';
import { COVERAGE_LIVE } from '@tsr/ui';
import { openPopoutWindow } from './popout';

/** A working MJPEG load should produce a first frame well inside this window.
 *  If it doesn't (notably: headless Chromium, which never renders an
 *  multipart/x-mixed-replace <img>, and any browser that can reach the proxy
 *  but won't decode multipart), we fall back to the still snapshot rather than
 *  spinning forever — the founder explicitly rejected perpetual-spinner fake
 *  "live" states (PR #87). Generous enough not to trip a slow-but-working feed. */
const MJPEG_FIRST_FRAME_TIMEOUT_MS = 8000;

interface MjpegPlayerProps {
  cameraId: string;
  name: string;
  /** Show the "open in new window" pop-out control (Phase V). */
  enablePopout?: boolean;
  /** Wall fill mode (#232): fill the parent edge-to-edge (no card border /
   *  radius / fixed aspect), frame centre-crops, our LIVE badge + retry button
   *  dropped (the wall caption owns state and the wall is read-only). Default
   *  false = panel card, unchanged. */
  fill?: boolean;
  /** Reports the player's honest runtime up to a host caption (#232). The
   *  snapshot-fallback state reports offline — live video isn't flowing. */
  onPlaybackState?: (state: 'loading' | 'playing' | 'offline') => void;
}

/** The shared card-vs-fill shell classes (see `fill` prop). */
const shellClass = (fill: boolean): string =>
  fill
    ? 'relative h-full w-full overflow-hidden bg-black'
    : 'relative w-full overflow-hidden rounded border border-slate-700 bg-black';
const shellStyle = (fill: boolean): React.CSSProperties | undefined =>
  fill ? undefined : { aspectRatio: '16 / 9' };

type PlayerState = 'loading' | 'playing' | 'error';

/**
 * Live player for an `mjpeg` camera (Axis VAPIX `video.cgi` — Tomahawk + the
 * Trail BC airfield cams). The browser renders multipart/x-mixed-replace
 * natively as an <img> src: it holds the connection open and swaps the frame as
 * each part arrives — no <video>, no hls.js. We always route through the API
 * proxy (`/api/cameras/:id/stream`) so the browser only ever talks same-origin;
 * the proxy carries the device's incomplete-TLS chain + any http→https mixed
 * content. See apps/api/src/routes/stream-proxy.ts (streamMjpeg).
 *
 * ⚠️ HEADLESS LIMITATION: Chromium in headless capture mode does NOT render
 * multipart MJPEG in an <img> — it hangs with no first-frame `load` and no
 * `error`. That is a *display* constraint, not a feed failure; this component
 * is therefore unverifiable in the preview browser and is covered by unit tests
 * + an API-side content-type probe. A real browser renders it live (founder
 * visual-confirm). To keep the headless / can't-decode case honest, a
 * first-frame timeout drops to the snapshot still rather than faking a badge.
 *
 * - first frame loads → LIVE badge, motion video.
 * - error OR no first frame within the timeout → snapshot fallback + retry.
 */
export function MjpegPlayer({
  cameraId,
  name,
  enablePopout = true,
  fill = false,
  onPlaybackState,
}: MjpegPlayerProps): JSX.Element {
  const [state, setState] = useState<PlayerState>('loading');
  const [attempt, setAttempt] = useState(0);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Honest runtime → host caption (#232): the snapshot fallback is offline.
  useEffect(() => {
    if (!onPlaybackState) return;
    onPlaybackState(state === 'error' ? 'offline' : state);
  }, [state, onPlaybackState]);

  // Always proxy: the upstream is the device's video.cgi, which may be http://
  // (mixed-content-blocked) or ship an incomplete TLS chain — the same-origin
  // proxy resolves both. Cache-bust per attempt so a manual retry re-opens the
  // (otherwise no-store, connection-reusing) stream.
  const base = `/api/cameras/${cameraId}/stream`;
  const src = attempt === 0 ? base : `${base}?_=${attempt}`;

  // Drive the <img> src + its load/error handlers IMPERATIVELY from an effect,
  // rather than declaratively via JSX `src={src}`. This is load-bearing for the
  // dev build: React 18 StrictMode mounts → unmounts → remounts every component,
  // which fires the cleanup below (`img.src=''`, to abort the never-ending MJPEG
  // socket). With a declarative `src={src}`, React never re-commits the prop on
  // the StrictMode remount (its virtual value never changed), so the <img> is
  // left blanked and never loads — the multipart feed reads as "Live video
  // unavailable" even though it renders perfectly in a real browser (verified:
  // raw <img> first-frames in ~600ms). Re-assigning src inside the effect makes
  // the remount re-open the stream. Mirrors LiveCameraPlayer, which (re)sets
  // video.src inside its own effect for exactly this reason.
  //
  // An `active` flag + nulled handlers during teardown ensure the socket-abort
  // `src=''` can't fire a spurious 'error' that would flip us to the snapshot
  // fallback after a clean teardown.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return undefined;
    let active = true;
    img.onload = (): void => {
      if (active) setState('playing');
    };
    img.onerror = (): void => {
      if (active) setState('error');
    };
    img.src = src;
    return () => {
      active = false;
      img.onload = null;
      img.onerror = null;
      // Release the (never-ending) MJPEG socket: clearing the src aborts the
      // in-flight request so a closed panel doesn't hold the device stream open.
      img.src = '';
    };
  }, [src]);

  // First-frame watchdog: if neither load nor error fires (headless / a browser
  // that won't decode multipart), fall back to the still rather than spin. A
  // successful load clears it via the state guard below.
  useEffect(() => {
    if (state !== 'loading') return undefined;
    const t = setTimeout(() => setState('error'), MJPEG_FIRST_FRAME_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [state, attempt]);

  if (state === 'error') {
    // failure → snapshot fallback. These Axis devices still serve a fresh JPEG
    // at image.cgi (proxied via /snapshot, independent of stream_url), so the
    // operator keeps an honest current frame — just not motion — plus a retry.
    return (
      <div className={shellClass(fill)} style={shellStyle(fill)} data-testid="mjpeg-player">
        <img
          src={`/api/cameras/${cameraId}/snapshot`}
          alt={`Latest frame from ${name}`}
          data-testid="mjpeg-snapshot-fallback"
          className={`absolute inset-0 h-full w-full ${fill ? 'object-cover' : 'object-contain'}`}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-black/60 px-2 py-1.5">
          <span className="text-xs text-slate-300">Live video unavailable — latest frame</span>
          {!fill && (
            <button
              type="button"
              onClick={() => {
                setState('loading');
                setAttempt((a) => a + 1);
              }}
              className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-slate-100 hover:bg-slate-700"
            >
              Retry stream
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass(fill)} style={shellStyle(fill)} data-testid="mjpeg-player">
      <img
        key={attempt}
        ref={imgRef}
        data-testid="mjpeg-stream"
        // src + load/error are driven imperatively from the effect above (see the
        // StrictMode note) — NOT declaratively here, or the dev remount blanks it.
        alt={`Live MJPEG from ${name}`}
        className={`h-full w-full bg-black ${fill ? 'object-cover' : 'object-contain'}`}
      />

      {enablePopout && (
        <button
          type="button"
          data-testid="popout-button"
          onClick={() => openPopoutWindow(cameraId)}
          title="Open in new window"
          aria-label="Open in new window"
          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded bg-black/60 text-slate-200 hover:bg-black/80"
        >
          {/* up-right arrow into a box — "pop out" affordance */}
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 4h6v6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20 4l-8 8" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {state === 'playing' && !fill && (
        <div
          data-testid="live-badge"
          className="absolute left-2 top-2 flex items-center gap-1.5 rounded bg-black/60 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider"
          style={{ color: COVERAGE_LIVE }}
        >
          <span
            className="inline-block h-2 w-2 animate-pulse rounded-full"
            style={{ backgroundColor: COVERAGE_LIVE }}
          />
          LIVE
        </div>
      )}

      {state === 'loading' && (
        <div
          data-testid="mjpeg-loading"
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-300"
        >
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-500 border-t-transparent" />
          <span className="text-xs">Connecting to live stream…</span>
        </div>
      )}
    </div>
  );
}
